import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { LogOut, Download, RefreshCw, Lock } from "lucide-react";
import { Logo } from "@/components/landing/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "or_admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchLeads = useCallback(async (tk) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      setLeads(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setToken("");
      setLeads(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchLeads(token);
  }, [token, fetchLeads]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Sesión iniciada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Contraseña incorrecta");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setLeads(null);
  };

  const exportCsv = () => {
    if (!leads?.length) return;
    const header = "Fecha;Nombre;Teléfono;Email;Tamaño;Cuándo;Tipo;Contacto;Mensaje;Origen";
    const rows = leads.map((l) =>
      [l.created_at, l.name, l.phone, l.email, l.size, l.when, l.client_type, l.contact_pref, (l.message || "").replace(/[\n;]/g, " "), l.source]
        .map((v) => `"${v ?? ""}"`)
        .join(";")
    );
    const blob = new Blob(["﻿" + [header, ...rows].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `leads-otraroom-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-brand-ink flex items-center justify-center px-5">
        <motion.form
          data-testid="admin-login-form"
          onSubmit={login}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-sm bg-brand-inkcard border border-white/10 rounded-3xl p-8"
        >
          <Logo dark />
          <h1 className="font-display font-extrabold text-white text-xl mt-8">Panel de leads</h1>
          <p className="text-white/50 text-sm mt-1">Acceso solo para administración.</p>
          <div className="relative mt-6">
            <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Contraseña"
              className="w-full bg-brand-ink border border-white/15 rounded-2xl pl-11 pr-4 py-3.5 text-white placeholder:text-white/35 outline-none focus:border-brand-yellow transition-colors"
            />
          </div>
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="mt-5 w-full bg-brand-yellow text-brand-ink font-display font-extrabold tracking-wide py-3.5 rounded-full hover:bg-brand-yellowhover transition-colors disabled:opacity-60"
          >
            {loading ? "ENTRANDO..." : "ENTRAR"}
          </button>
          <a href="/" className="block text-center text-white/40 text-xs mt-5 hover:text-brand-yellow transition-colors">
            ← Volver a la web
          </a>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-smoke">
      <header className="bg-brand-ink text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between gap-4">
          <Logo dark />
          <div className="flex items-center gap-2.5">
            <button
              data-testid="admin-refresh-button"
              onClick={() => fetchLeads(token)}
              className="p-2.5 rounded-xl border border-white/15 hover:border-brand-yellow hover:text-brand-yellow transition-colors"
              aria-label="Recargar leads"
            >
              <RefreshCw size={17} />
            </button>
            <button
              data-testid="admin-export-button"
              onClick={exportCsv}
              className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl border border-white/15 hover:border-brand-yellow hover:text-brand-yellow transition-colors"
            >
              <Download size={16} /> CSV
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl bg-brand-yellow text-brand-ink hover:bg-brand-yellowhover transition-colors"
            >
              <LogOut size={16} /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-display font-extrabold text-3xl text-brand-ink tracking-tight">
          Solicitudes recibidas
          {leads && (
            <span data-testid="admin-leads-count" className="ml-3 align-middle font-mono text-sm font-bold bg-brand-yellow text-brand-ink px-3 py-1 rounded-full">
              {leads.length}
            </span>
          )}
        </h1>

        {loading && <p className="mt-8 text-brand-ink/50 text-sm">Cargando...</p>}

        {!loading && leads && leads.length === 0 && (
          <p data-testid="admin-no-leads" className="mt-8 text-brand-ink/50">Aún no hay solicitudes. Las que lleguen desde el formulario aparecerán aquí.</p>
        )}

        {!loading && leads && leads.length > 0 && (
          <div className="mt-8 bg-white rounded-3xl border border-brand-ink/8 overflow-hidden shadow-[0_8px_30px_rgba(18,19,22,0.05)]">
            <div className="overflow-x-auto">
              <table data-testid="admin-lead-table" className="w-full text-sm min-w-[1150px]">
                <thead>
                  <tr className="bg-brand-ink text-white/80 font-mono text-[11px] tracking-[0.15em] uppercase text-left">
                    <th className="px-5 py-4">Fecha</th>
                    <th className="px-5 py-4">Nombre</th>
                    <th className="px-5 py-4">Teléfono</th>
                    <th className="px-5 py-4">Email</th>
                    <th className="px-5 py-4">Tamaño</th>
                    <th className="px-5 py-4">Cuándo</th>
                    <th className="px-5 py-4">Tipo</th>
                    <th className="px-5 py-4">Contacto</th>
                    <th className="px-5 py-4">Mensaje</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id} data-testid={`admin-lead-row-${lead.id}`} className="border-t border-brand-ink/6 hover:bg-brand-yellowsoft/30 transition-colors">
                      <td className="px-5 py-4 text-brand-ink/55 whitespace-nowrap">
                        {new Date(lead.created_at).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td className="px-5 py-4 font-semibold text-brand-ink whitespace-nowrap">{lead.name}</td>
                      <td className="px-5 py-4 whitespace-nowrap">
                        <a href={`tel:${lead.phone}`} className="text-brand-ink underline decoration-brand-yellow decoration-2 underline-offset-2">{lead.phone}</a>
                      </td>
                      <td className="px-5 py-4 text-brand-ink/70">{lead.email}</td>
                      <td className="px-5 py-4">
                        <span className="font-mono text-xs font-bold bg-brand-smoke px-2.5 py-1 rounded-full">{lead.size}</span>
                      </td>
                      <td className="px-5 py-4 text-brand-ink/70 whitespace-nowrap">{lead.when || "—"}</td>
                      <td className="px-5 py-4 text-brand-ink/70 whitespace-nowrap">{lead.client_type || "—"}</td>
                      <td className="px-5 py-4">
                        <span className="font-mono text-xs font-bold bg-brand-yellow/50 px-2.5 py-1 rounded-full whitespace-nowrap">{lead.contact_pref || "—"}</span>
                      </td>
                      <td className="px-5 py-4 text-brand-ink/60 max-w-[280px] truncate" title={lead.message}>{lead.message || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
